import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

export const TimeInput = (props: any) => {
  const {
    className,
    label,
    id,
    placeholder,
    middle,
    disabled,
    input,
    lastLabel,
    meta,
    onChange,
    onBlur,
    testId,
  } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastValue = useRef("");
  const [internalValue, setInternalValue] = useState<any>("");
  useEffect(() => {
    setInternalValue(input.value);
  }, [input.value]);

  const value = useMemo(
    () => input?.value || internalValue,
    [input?.value, internalValue]
  );
  const pattern = useMemo(() => "--:--".slice(value?.length || 0, 6), [value]);

  const [onChangeFlag, setonChangeFlag] = useState(false);

  const isValidHour = (h: number) => Number.isInteger(h) && h >= 0 && h < 24;
  const isValidMinutes = (m: number) =>
    (Number.isInteger(m) && m >= 0 && m < 60) || Number.isNaN(m);

  const isValidTime = useCallback((val: string) => {
    const [hoursStr, minutesStr] = val.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (!isValidHour(hours) || !isValidMinutes(minutes)) return false;
    if (hours < 10 && Number(hoursStr[0]) > 2) return false;
    if (minutes < 10 && Number(minutesStr[0]) > 5) return false;

    return true;
  }, []);

  const handleTimeValidation = useCallback(
    (val: string) => {
      const regexp = /^\d{0,2}?:?\d{0,2}$/;
      if (!regexp.test(val)) {
        return null;
      }

      const len = val.length;
      const lastLen = lastValue.current.length;

      if (isValidTime(val)) {
        if (len === 2 && lastLen !== 3 && val.indexOf(":") === -1) {
          val = val + ":";
        }
        if (len === 2 && lastLen === 3) {
          val = val.slice(0, 1);
        }
      } else if (len > 0 && Number(val[0]) > 2) {
        if (val.indexOf(":") === -1) val = "0" + val[0] + ":" + val.slice(1);
        else if (len === 5) val = "0" + val.slice(1);
        else val = "0" + val;
      } else if (len === 2 && Number(val[0]) === 2 && Number(val[1]) > 3) {
        val = "23:";
      } else if (len > 2 && val.indexOf(":") === -1) {
        val = val.slice(0, 2) + ":" + val.slice(2);
      } else if (len === 4 && Number(val[3]) > 5) {
        val = val.slice(0, 3) + "0" + val[3];
      }

      lastValue.current = val;

      return val;
    },
    [lastValue, isValidTime]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setonChangeFlag(true);
      const checkedValue = handleTimeValidation(event.target.value);
      if (event.target.value === "" || checkedValue) {
        setInternalValue(checkedValue);
        if (input) input.onChange(checkedValue);
        if (onChange) onChange(input.name, checkedValue);
      }
    },
    [input, handleTimeValidation, onChange]
  );

  const handleBlur = useCallback(() => {
    if (!input || !onChangeFlag) return;
    let value = internalValue;

    const [hoursStr, minutesStr] = internalValue.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (internalValue?.length === 1) value = "0" + internalValue[0] + ":00";
    if (internalValue?.length === 3) {
      if (internalValue.indexOf(":") === 0) value = "00" + internalValue;
      if (internalValue.indexOf(":") === 1) value = "0" + internalValue + "0";
      if (internalValue.indexOf(":") === 2) value = internalValue + "00";
    }
    if (internalValue?.length === 4) value = internalValue + "0";
    if (!isValidHour(hours)) value = "23:" + value.slice(3);
    if (!isValidMinutes(minutes)) value = value.slice(0, 2) + ":59";

    if (value) input.onChange(value);
    if (onBlur) onBlur();
    input.onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue, input]);

  const handlePatternClick = useCallback(() => {
    if (inputRef?.current) inputRef.current.focus();
  }, []);

  return (
    <div
      className={classNames({
        "has-error": meta.error && meta.touched,
      })}
    >
      <label
        htmlFor={id}
        className={classNames(
          middle ? "label-Admin-Pickers-middle" : "label-Admin-Pickers",
          {
            labelError: meta.error && meta.touched,
            disabled: disabled,
            "last-label": lastLabel,
          }
        )}
        data-testId={`${testId}-label`}
      >
        {label}
      </label>
      <div className="time-input-container">
        <input
          ref={inputRef}
          className={classNames(className, {
            disabled: disabled,
            inputError: meta.error && meta.touched,
          })}
          {...input}
          disabled={disabled}
          value={!disabled ? value : value || "-"}
          id={id}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={5}
          autoComplete="off"
          data-testId={testId}
        />
        {value?.length > 0 && (
          <p className="time-input-pattern" onClick={handlePatternClick}>
            {pattern}
          </p>
        )}
      </div>
      {meta.error && meta.touched && (
        <div className="input-errors-container">
          <p className="errors" data-testId={`${testId}-error`}>
            {meta.error}
          </p>
        </div>
      )}
    </div>
  );
};
